/**
 * Client-side preprocessing for listing photo uploads.
 *
 * Phone cameras routinely produce 4–12 MB files. Uploading those raw is the
 * main cause of slow/failed uploads on mobile data, and it's wasted bytes:
 * the largest variant we ever render is 2400px wide (`listing-card-6x`, see
 * configLayout), so anything beyond that is downscaled server-side anyway.
 *
 * Compression is best-effort by design — if it fails for any reason we hand
 * back the ORIGINAL file rather than blocking the upload. A slightly slower
 * upload beats a seller who can't publish.
 */
import imageCompression from 'browser-image-compression';

// `image/jpg` is non-standard but some older systems / browsers do emit it,
// so accept both as a belt-and-braces fallback.
export const ACCEPTED_IMAGE_TYPES = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif'];

// iPhones shoot HEIC by default. We can't upload it as-is (the API rejects
// it), but we CAN transcode it to JPEG below, so it has to survive both the
// file picker and the pre-flight type check.
export const TRANSCODE_IMAGE_TYPES = ['image/heic', 'image/heif'];

// The `accept` attribute deliberately uses the broad `image/*` rather than an
// explicit type list. A narrow list is what broke mobile: iOS and Android
// picker behaviour around HEIC is inconsistent, and files the OS won't match
// are greyed out with no explanation — which reads to the seller as "bulk
// upload is broken". Let the OS offer anything image-shaped; the pre-flight
// check below is what actually decides.
export const ACCEPT_IMAGES = 'image/*';

// SVG is the one image type we refuse: it can carry script, and it isn't a
// photo of a garment. Everything else image-shaped gets a try — the API is
// still the final authority.
const REJECTED_IMAGE_TYPES = ['image/svg+xml'];

export const isAcceptedImageType = file => {
  const type = file?.type || '';
  return type.startsWith('image/') && !REJECTED_IMAGE_TYPES.includes(type);
};

/** HEIC/HEIF must be re-encoded before upload, whatever its size. */
export const needsTranscode = file => TRANSCODE_IMAGE_TYPES.includes(file?.type);

// Below this, compressing costs more (CPU, battery, a decode/encode round
// trip) than it saves.
const COMPRESS_ABOVE_BYTES = 1.5 * 1024 * 1024;
// Matches the widest variant we request, so we never discard visible detail.
const MAX_DIMENSION_PX = 2400;
const TARGET_MAX_MB = 1.5;
// Hard ceiling on how long we'll wait. The library can hang rather than throw
// (a wedged web worker, a browser that can't decode the file), and a seller
// staring at a stuck spinner is worse than an uncompressed upload.
const COMPRESS_TIMEOUT_MS = 10000;

/**
 * Animated GIFs must not be re-encoded — the canvas-based compressor keeps
 * only the first frame, silently turning an animation into a still.
 */
const shouldSkipCompression = file =>
  !file ||
  file.type === 'image/gif' ||
  // A small HEIC still has to go through the encoder — it's a format change,
  // not a size optimisation.
  (!needsTranscode(file) && file.size <= COMPRESS_ABOVE_BYTES);

/**
 * Downscale/re-encode an oversized photo before upload.
 *
 * @param {File} file
 * @returns {Promise<File>} the compressed file, or the original when
 *   compression is unnecessary, unhelpful, or fails.
 */
export const compressImageForUpload = async file => {
  if (shouldSkipCompression(file)) {
    return file;
  }
  const isTranscode = needsTranscode(file);
  const outputType = isTranscode ? 'image/jpeg' : file.type;
  const outputName = isTranscode ? file.name.replace(/\.(heic|heif)$/i, '.jpg') : file.name;
  let timeoutId = null;
  try {
    const compressed = await Promise.race([
      imageCompression(file, {
        maxSizeMB: TARGET_MAX_MB,
        maxWidthOrHeight: MAX_DIMENSION_PX,
        useWebWorker: true,
        // Keep the original container, except for HEIC/HEIF which has to come
        // out as JPEG to be uploadable at all.
        fileType: outputType,
      }),
      new Promise(resolve => {
        timeoutId = setTimeout(() => resolve(null), COMPRESS_TIMEOUT_MS);
      }),
    ]);
    if (!compressed) {
      return file;
    }
    // browser-image-compression can return a Blob; normalise back to a File so
    // the SDK still sees a filename.
    const asFile =
      compressed instanceof File && !isTranscode
        ? compressed
        : new File([compressed], outputName, { type: outputType });
    // A transcode must be kept even when it came out bigger — the original is
    // not uploadable. For a plain compression, never send more bytes than we
    // started with (it can overshoot on already-optimised images).
    if (isTranscode) {
      return asFile.size > 0 ? asFile : file;
    }
    return asFile.size > 0 && asFile.size < file.size ? asFile : file;
  } catch (e) {
    return file;
  } finally {
    if (timeoutId) {
      clearTimeout(timeoutId);
    }
  }
};
